import React from 'react';
import { Container, Row, Col, Card, Table, Alert } from 'react-bootstrap';
import './style.css';


const DataExtractionView = ({ output }) => {
    if (!output || !output.accounts) {
        return null;
    }

    const formatDate = (dateString) => {
        if (!dateString) return dateString;
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        return `${day}-${month}-${year}`;
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return dateTimeString;
        const [datePart, timePart] = dateTimeString.split('T');
        const [year, month, day] = datePart.split('-');
        const time = timePart.split('.')[0];
        return `${day}-${month}-${year} ${time}`;
    };

    // Simple currency formatter
    const formatCurrencyAmount = (amount, currency, decimals=2) => {
        return `${currency} ${formatNumber(amount, decimals)}`;
    };

    // Format number with thousands separator and no unnecessary decimals
    const formatNumber = (amount, decimals=2) => {
        if (amount === null || amount === undefined) return '';
        const num = parseFloat(amount);
        // Use toLocaleString to add thousands separator
        // Remove decimals if they are .00
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    // Check if transactions have enrichment data
    const isEnriched = (transactions) => {
        return transactions && transactions.length > 0 && transactions[0].category_id !== undefined;
    };

    return (
        <Container fluid className="mt-4 data-extraction-container">
            {/* Header Information */}
            <Card className="mb-4">
                <Card.Header className="text-start">
                    <h4 className="mb-0">Statement Summary</h4>
                </Card.Header>
                <Card.Body className="text-start">
                    <Row>
                        <Col md={4}>
                            <p><strong>Account Holder: </strong>{output.account_holder_name}</p>
                            <p><strong>Provider: </strong>{output.provider_id?.replace('_', ' ').toUpperCase()}</p>
                            <p><strong>Statement Date: </strong>{formatDate(output.statement_date)}</p>
                        </Col>
                        <Col md={4}>
                            <p><strong>Total Processing Time: </strong>{output.total_process_duration}s</p>
                            {output.extraction_duration && (
                                <p><strong>Extraction Time: </strong>{output.extraction_duration}s</p>
                            )}
                            {output.enrichment_duration ? (
                                <p><strong>Enrichment Time: </strong>{output.enrichment_duration}s</p>
                            ) : null}
                            {output.used_ocr !== undefined && (
                                <p><strong>Used OCR: </strong>{output.used_ocr ? 'Yes' : 'No'}</p>
                            )}
                        </Col>
                        <Col md={4}>
                            <p><strong>Pages: </strong>{formatNumber(output.page_count, 0)}</p>
                            <p><strong>File Size: </strong>{(output.file_size / 1024).toFixed(2)} KB</p>
                            <p><strong>Process Date: </strong>{formatDateTime(output.process_datetime)}</p>
                        </Col>
                    </Row>
                    {output.is_valid ? (
                        <Alert variant="success">
                            <i className="fas fa-check-circle me-1"></i>
                            Statement successfully validated
                        </Alert>
                    ) : (
                        <Alert variant="danger">
                            <i className="fas fa-times-circle me-1"></i>
                            Statement validation issues detected
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Account Cards */}
            {output.accounts.map((account, index) => (
                <Card key={index} className="mb-4">
                    <Card.Header className="text-start">
                        <h4 className="mb-0">{output.accounts.length === 1 ? 'Account' : `Account ${index + 1}`}</h4>
                    </Card.Header>
                    <Card.Body className="text-start">
                        {/* Account Information Row */}
                        <Row className="mb-4">
                            <Col lg={4}>
                                <Card className="h-100">
                                    <Card.Header className="bg-light text-start">
                                        <h6 className="mb-0">Account Details</h6>
                                    </Card.Header>
                                    <Card.Body className="text-start">
                                        <p><strong>Name: </strong>{account.account_name}</p>
                                        <p><strong>Number: </strong>{account.account_number}</p>
                                        <p><strong>Type: </strong>{account.account_type?.replace('_', ' ').toUpperCase()}</p>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Card Details (if available) */}
                            {account.card_details && (
                                <Col lg={4}>
                                    <Card className="h-100">
                                        <Card.Header className="bg-light text-start">
                                            <h6 className="mb-0">Card Details</h6>
                                        </Card.Header>
                                        <Card.Body className="text-start">
                                            <p><strong>Payment Due: </strong>{formatDate(account.card_details.payment_due_date)}</p>
                                            <p><strong>Due Amount: </strong>{formatCurrencyAmount(account.card_details.payment_due_amount, account.currency)}</p>
                                            <p><strong>Minimum Payment: </strong>{formatCurrencyAmount(account.card_details.minimum_payment_amount, account.currency)}</p>
                                            {account.card_details.rewards_balances && (
                                                <div>
                                                    <strong>Rewards: </strong>
                                                    {Object.entries(account.card_details.rewards_balances).map(([type, balance]) => (
                                                        <span key={type} className="ms-2">
                                                            {formatNumber(balance, 0)} {type}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {/* Statement Summary */}
                            <Col lg={account.card_details ? 4 : 8}>
                                <Card className="h-100">
                                    <Card.Header className="bg-light text-start">
                                        <h6 className="mb-0">Account Summary</h6>
                                    </Card.Header>
                                    <Card.Body className="text-start">
                                        <p><strong>Starting Balance: </strong> 
                                            <span className={account.starting_balance >= 0 ? 'text-success' : 'text-danger'}>
                                                {formatCurrencyAmount(account.starting_balance, account.currency, 0)}
                                            </span>
                                        </p>
                                        <p><strong>Closing Balance: </strong> 
                                            <span className={account.closing_balance >= 0 ? 'text-success' : 'text-danger'}>
                                                {formatCurrencyAmount(account.closing_balance, account.currency, 0)}
                                            </span>
                                        </p>
                                        <p><strong>Currency: </strong>{account.currency}</p>
                                        <p><strong>Transactions: </strong>{formatNumber(account.transactions?.length, 0) || 0}</p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Transactions Table */}
                        {account.transactions && account.transactions.length > 0 && (
                            <div className="text-start">
                                <h5 className="mb-3">Transactions</h5>
                                <div style={{ overflowX: 'auto' }}>
                                    <Table striped hover className="mb-0" style={{ textAlign: 'left', minWidth: isEnriched(account.transactions) ? '2000px' : '1200px' }}>
                                        <thead className="table-success">
                                            <tr>
                                                <th style={{ textAlign: 'left', width: '200px' }}>Date</th>
                                                <th style={{ textAlign: 'left', width: '500px' }}>Description</th>
                                                <th style={{ textAlign: 'right', width: '120px' }}>Amount</th>
                                                <th style={{ textAlign: 'right', width: '120px' }}>Balance</th>
                                                <th style={{ textAlign: 'left', width: '80px' }}>Currency</th>
                                                <th style={{ textAlign: 'left', width: '80px' }}>Debit/Credit</th>
                                                {isEnriched(account.transactions) && (
                                                    <>
                                                        <th style={{ textAlign: 'left', width: '140px' }}>Transact. Type</th>
                                                        <th style={{ textAlign: 'left', width: '120px' }}>Income Type</th>
                                                        <th style={{ textAlign: 'left', width: '140px' }}>Meta Category</th>
                                                        <th style={{ textAlign: 'left', width: '140px' }}>Category</th>
                                                        <th style={{ textAlign: 'left', width: '500px' }}>Merchant</th>
                                                        <th style={{ textAlign: 'left', width: '80px' }}>Essential</th>
                                                        <th style={{ textAlign: 'left', width: '80px' }}>Late Payment</th>
                                                        <th style={{ textAlign: 'left', width: '80px' }}>Orig. Amount</th>
                                                        <th style={{ textAlign: 'left', width: '80px' }}>Orig. Currency</th>
                                                        <th style={{ textAlign: 'left', width: '80px' }}>Country</th>
                                                    </>
                                                )}
                                                <th style={{ textAlign: 'left', width: '100px' }}>ID</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {account.transactions.map((transaction, txIndex) => (
                                                <tr key={transaction.id || txIndex}>
                                                    <td>{formatDate(transaction.date)}</td>
                                                    <td>{transaction.description}</td>
                                                    <td className="text-end">
                                                        {formatNumber(transaction.amount)}
                                                    </td>
                                                    <td className="text-end">
                                                        {formatNumber(transaction.balance)}
                                                    </td>
                                                    <td>{transaction.currency}</td>
                                                    <td>{transaction.debit_credit}</td>
                                                    {isEnriched(account.transactions) && (
                                                        <>
                                                            <td>{transaction.transaction_type || '-'}</td>
                                                            <td>{transaction.income_type?.replace(/_/g, ' ') || '-'}</td>
                                                            <td>{transaction.meta_category_id?.replace(/_/g, ' ') || '-'}</td>
                                                            <td>{transaction.category_id?.replace(/_/g, ' ') || '-'}</td>
                                                            <td>{transaction.merchant_name || '-'}</td>
                                                            <td>{transaction.is_essential !== undefined ? (transaction.is_essential ? 'Yes' : 'No') : '-'}</td>
                                                            <td>{transaction.is_late_payment_charge !== undefined ? (transaction.is_late_payment_charge ? 'Yes' : 'No') : '-'}</td>
                                                            <td>{transaction.original_amount ? formatNumber(transaction.original_amount) : '-'}</td>
                                                            <td>{transaction.original_currency || '-'}</td>
                                                            <td>{transaction.country_code || '-'}</td>
                                                        </>
                                                    )}
                                                    <td>
                                                        <small className="text-muted">
                                                            {transaction.id?.substring(0, 8)}...
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            ))}
        </Container>
    );
};

export default DataExtractionView;